<template>
    <div class="device-card" @click="selectDeviceRelation" :class="{ 'cursor': type == 'sensor', 'lost-connection': hasLostConnection }">
        <div class="device-title">
            <div class="info">
                <span class="device-icon" :class="type == 'sensor' ? 'sensor' : 'receptor'"></span>
                <span class="device-name">S/N {{ device && device.ref ? device.ref : '-' }}</span>
            </div>
        </div>
        <div class="device-info">
            <span class="device-last-conection">{{$t('config.sensor.devices.last_communication')}} {{ lastActivityLabel }}</span>

            <div class="device-properties">
                <RadioIcon :radio="device && device.metadata ? device.metadata.radio : null" :lostConnection="hasLostConnection" />
                <BatteryIcon v-if="type == 'sensor'" :battery="device && device.metadata ? device.metadata.battery : 0" :lostConnection="hasLostConnection" />
            </div>
        </div>
        <div class="device-connection-type" v-if="type == 'receptor' && device && device.metadata && device.metadata.connection_type">
            <span class="connection-type-text">{{$t('config.sensor.devices.connection_type')}}</span> 
            <span class="wifi" v-if="device.metadata.connection_type == 'CT_WIFI'"></span>
            <span class="ethernet" v-else-if="device.metadata.connection_type == 'CT_ETHERNET'"></span>
            <span class="unknown" v-else>{{$t('config.sensor.devices.unknown')}}</span> 
        </div>
        <div class="device-gateway" v-if="related && related.name">
            <span class="device-gateway-icon"></span>
            <span class="device-gateway-text">{{ related.name }}</span>
        </div>
    </div>
</template>
<script>
import BatteryIcon from './batteryIcon.vue';
import RadioIcon from './radioIcon.vue';

export default {
    components: {
        BatteryIcon,
        RadioIcon
    },
    name: 'DeviceCard',
    props: {
        device: {
            type: Object,
            required: true
        },
        related: {
            type: Object,
            default: null
        },
        type: {
            type: String,
            default: 'sensor'
        }
    },
    data() {
        return {
            lostConnectionMs: 60 * 60 * 1000,
            unixSecondsMax: 1e11
        }
    },
    computed: {
        linkedSensor() {
            const sensors = this.$store.getters['sensor/getList']()
            if (!this.device || !sensors) {
                return null
            }

            if (this.device.id && sensors[this.device.id]) {
                return sensors[this.device.id]
            }

            if (!this.device.ref) {
                return null
            }

            return Object.values(sensors).find((sensor) => sensor && sensor.ref === this.device.ref) || null
        },
        lastActivity() {
            const dates = []
            const deviceDate = this.lastValueDate(this.device)
            if (deviceDate !== null) {
                dates.push(deviceDate)
            }

            const linkedDate = this.lastValueDate(this.linkedSensor)
            if (linkedDate !== null) {
                dates.push(linkedDate)
            }

            const moments = dates.map((date) => this.parseLastActivity(date)).filter(Boolean)
            if (moments.length > 0) {
                return moments.reduce((latest, current) => (current.isAfter(latest) ? current : latest))
            }

            const lastCommunication = this.device && this.device.metadata ? this.device.metadata.last_communication : null
            return this.parseLastActivity(lastCommunication)
        },
        lastActivityLabel() {
            return this.lastActivity ? this.lastActivity.format('DD/MM/YYYY HH:mm') : '-'
        },
        hasLostConnection() {
            if (!this.lastActivity) {
                return false
            }

            return moment().diff(this.lastActivity) > this.lostConnectionMs
        }
    },
    methods: {
        lastValueDate(source) {
            if (source && source.last_value && source.last_value.date != null && source.last_value.date !== '') {
                return source.last_value.date
            }

            return null
        },
        parseLastActivity(value) {
            if (value === null || value === undefined || value === '' || value === 'null') {
                return null
            }

            if (typeof value === 'number' || (typeof value === 'string' && /^\d+(\.\d+)?$/.test(String(value).trim()))) {
                const timestamp = Number(value)
                if (!Number.isFinite(timestamp) || timestamp <= 0) {
                    return null
                }

                const parsedUnix = moment(timestamp < this.unixSecondsMax ? timestamp * 1000 : timestamp)
                return parsedUnix.isValid() ? parsedUnix : null
            }

            const parsed = moment(value)
            return parsed.isValid() ? parsed : null
        },
        selectDeviceRelation() {
            this.$emit('selectDeviceRelation', this.device, this.type, this.related)
        }
    }
}
</script>
<style lang="scss" scoped>
.device-card {
    position: relative;
    background: $neutro-light;
    padding: 8px;
    border-radius: 4px;
    border: 1px solid $neutro-light;
    width: 100%;
    @include display-flex();
    @include flex-direction(column);
    gap: 4px;
    &.cursor {
        cursor: pointer;
    }

    &.lost-connection {
        background: $error-t90;
        border-color: $error-t90;

        .device-title .info .device-name {
            color: $error-dark;
        }
    }

    .device-title {
        @include display-flex();
        @include align-items(center);
        @include justify-content(space-between);
        width: 100%;

        .info {
            @include display-flex();
            @include align-items(center);
            
            gap: 8px;
            flex: 1;
            min-width: 0;

            .device-icon {
                @include background($image: img('sensor_testo.svg'), $size: 16px, $position: center center);
                &.receptor {
                    @include background($image: img('receptor.svg'), $size: 16px, $position: center center);
                }
                width: 24px;
                padding: 4px;
                height: 24px;
                flex-shrink: 0;
                background-color: $color-neutral-200;
                border-radius: 4px;
            }

            .device-name {
                @include font-size(16px);
                font-family: $text-bold;
                color: $neutro-s80;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
    }
    .device-connection-type {
        @include display-flex();
        @include align-items(center);
        gap: 8px;
        .connection-type-text {
            color: $neutro-s60;
            font-family: $text;
            @include font-size(14px);
        }
        .wifi {
            @include background($image: img('wifi_full.svg'), $size: 16px, $position: center center);
            filter: contrast(0.3);
            width: 16px;
            height: 16px;
        }
        .ethernet {
            @include background($image: img('cable.svg'), $size: 16px, $position: center center);
            width: 16px;
            height: 16px;
        }
        .unknown {
            color: #B3B3B3;
            font-family: $text;
            @include font-size(14px);
        }
    }
    .device-info {
        @include display-flex();
        @include align-items(center);
        @include justify-content(space-between);
        width: 100%;

        .device-last-conection {
            color: $neutro-s60;
            font-family: $text;
            @include font-size(14px);
        }

        .device-properties {
            @include display-flex();
            @include align-items(center);
            gap: 8px;
        }
    }

    .device-gateway {
        @include display-flex();
        @include align-items(center);
        gap: 8px;
        border-radius: 4px;
        border: 1px solid $color-neutral-300;
        padding: 4px;
        background-color: $color-neutral-50;

        .device-gateway-icon {
            @include background($image: img('receptor_subitem.svg'), $size: 24px, $position: center center);
            background-color: $color-neutral-200;
            width: 24px;
            height: 24px;
        }

        .device-gateway-text {
            color: $color-neutral-600;
            font-family: $text;
            @include font-size(14px);
            font-family: $text;
        }
    }
}
</style>